import { useState } from "react";
import { Loader2 } from "lucide-react";
import { s } from "../style.js";
import type { Role } from "../types";

const kickerStyle = s(
  "font-size:13px; letter-spacing:0.16em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600)"
);

const fieldLabel = s(
  "font-size:12px; letter-spacing:0.12em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px"
);

const inputStyle = s(
  "width:100%; min-height:46px; padding:10px 14px; font:inherit; color:var(--color-text); background:var(--color-bg); border:2px solid var(--color-divider)"
);

export default function LoginPage({ onLogin }: { onLogin: (input: { mode: Role; password: string; username?: string }) => void | Promise<void> }) {
  const [mode, setMode] = useState<Role>("tablet");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectMode = (next: Role) => {
    setMode(next);
    setError("");
    setUsername("");
    setPassword("");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await onLogin(mode === "admin" ? { mode, username, password } : { mode, password });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to sign in");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={s(
        "width:100vw; height:100vh; background:var(--color-bg); color:var(--color-text); font-family:var(--font-body); display:flex; align-items:center; justify-content:center; overflow:hidden"
      )}
    >
      <div
        style={s(
          "width:420px; background:var(--color-surface); border:2px solid var(--color-divider); display:flex; flex-direction:column"
        )}
      >
        <div style={s("padding:28px 28px 24px; border-bottom:2px solid var(--color-divider)")}>
          <div style={s("display:flex; align-items:center; gap:14px; margin-bottom:22px")}>
            <span
              style={s(
                "width:56px; height:56px; flex:none; display:flex; align-items:center; justify-content:center; background:var(--color-accent); color:var(--on-accent); font-family:var(--font-heading); font-weight:800; font-size:30px; letter-spacing:-0.04em"
              )}
            >
              IT
            </span>
            <div>
              <div style={s("font-family:var(--font-heading); font-weight:800; font-size:26px; letter-spacing:-0.02em; line-height:1.1")}>
                Ops Display
              </div>
              <div style={kickerStyle}>Sign in required</div>
            </div>
          </div>

          <div style={s("display:grid; grid-template-columns:1fr 1fr; gap:0")}>
            {[
              { key: "tablet", label: "Tablet" },
              { key: "admin", label: "Admin" },
            ].map((opt) => {
              const active = mode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                   onClick={() => selectMode(opt.key as Role)}
                  style={{
                    ...s(
                      "all:unset; cursor:pointer; display:flex; align-items:center; justify-content:center; padding:12px 14px; border:2px solid var(--color-divider); font-family:var(--font-heading); font-weight:800; font-size:14px; letter-spacing:0.08em; text-transform:uppercase"
                    ),
                    marginLeft: opt.key === "admin" ? "-2px" : "0",
                    background: active ? "var(--color-text)" : "transparent",
                    color: active ? "var(--color-bg)" : "var(--color-text)",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={s("padding:24px 28px 28px")}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
              {error && (
                <div
                  style={s(
                    "margin-bottom:16px; padding:12px 14px; background:var(--color-accent); color:var(--on-accent); font-family:var(--font-heading); font-weight:800; font-size:13px; letter-spacing:0.08em; text-transform:uppercase"
                  )}
                >
                  {error}
                </div>
              )}

              {mode === "admin" && (
                <div style={s("margin-bottom:16px")}>
                  <div style={fieldLabel}>Username</div>
                  <input
                    autoFocus
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter admin username"
                    style={inputStyle}
                  />
                </div>
              )}

              <div style={s("margin-bottom:16px")}>
                <div style={fieldLabel}>Password</div>
                <input
                  autoFocus={mode === "tablet"}
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder={`Enter ${mode} password`}
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={s(
                  "all:unset; box-sizing:border-box; cursor:pointer; width:100%; display:flex; align-items:center; justify-content:center; padding:14px; background:var(--color-accent); color:var(--on-accent); border:2px solid var(--color-accent); font-family:var(--font-heading); font-weight:800; font-size:15px; letter-spacing:0.08em; text-transform:uppercase"
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" style={s("margin-right:6px")} />
                    Signing in...
                  </>
                ) : "Sign in"}
              </button>
            </form>
        </div>
      </div>
    </div>
  );
}
